// SCR-STAFF-204 — تعرفه مصوب (US-CAT-030 تا 033): نسخه‌های منتشرشده کتاب ارزش نسبی و ضرایب K همراه وضعیت پذیرش مستأجر و
// فعال‌سازی خودکار (AC-CAT-030.1، 030.3)، گزارش اثر یک نسخه پیش از فعال‌سازی (AC-CAT-030.1)، فعال‌سازی با cat.tariff.manage
// (AC-CAT-030.2) و مقایسه قیمت مرکز با تعرفه مصوب (AC-CAT-033.1؛ BR-CAT-035). داده نسخه در PLT است و در مستأجر ویرایش
// نمی‌شود (BR-CAT-030)؛ نسخه نمونه با نشان «نمونه» نمایش داده می‌شود (CLAUDE.md §10).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type TariffBookVersionView,
  type TariffComparisonRow,
  type TariffImpactRow,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  JalaliDate,
  ListPage,
  Money,
  SampleBadge,
  StatusChip,
  faDigits,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { WriteNotice } from '../settings/common';
import { catKeys, useBranches } from './common';

export const SCR_STAFF_204 = 'SCR-STAFF-204';

/** درصد × ۱۰۰ به درصد نمایشی (۱۲۵۰ ← ۱۲٫۵). */
function percent(basisPoints: string | number | null | undefined): string | null {
  return basisPoints === null || basisPoints === undefined
    ? null
    : faDigits(Math.round(Number(basisPoints)) / 100);
}

export function TariffBookPage() {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.tariff.manage');
  const idempotency = useIdempotencyKey();
  const branches = useBranches();
  const [branchId, setBranchId] = useState('');
  const [impactOf, setImpactOf] = useState<TariffBookVersionView | null>(null);
  const [activating, setActivating] = useState<TariffBookVersionView | null>(null);

  const versions = useQuery({
    queryKey: catKeys.tariffVersions(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/tariff-book-versions')),
  });

  const impact = useQuery({
    enabled: impactOf !== null,
    queryKey: catKeys.tariffImpact(session?.tenantId, impactOf?.dataset ?? '', Number(impactOf?.version ?? 0), branchId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/tariff-book-versions/{dataset}/{version}/impact', {
          params: {
            path: { dataset: impactOf?.dataset ?? '', version: Number(impactOf?.version ?? 0) },
            query: { branchId: branchId || undefined },
          },
        }),
      ),
  });

  const comparison = useQuery({
    queryKey: catKeys.tariffComparison(session?.tenantId, branchId),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/catalog/tariff-comparison', {
          params: { query: { branchId: branchId || undefined } },
        }),
      ),
  });

  const activate = useMutation({
    mutationFn: (version: TariffBookVersionView) =>
      unwrap(
        tenantApi.POST('/api/v1/catalog/tariff-book-versions/:activate', {
          body: { dataset: version.dataset, version: version.version },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      setActivating(null);
      setImpactOf(null);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
  });

  const versionColumns: DataTableColumn<TariffBookVersionView>[] = [
    { id: 'dataset', headerKey: 'cat:tariff.columns.dataset', cell: (row) => t(`cat:tariff.dataset.${row.dataset}`) },
    {
      id: 'version',
      headerKey: 'cat:tariff.columns.version',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{faDigits(row.version)}</span>
          {row.isSample && <SampleBadge />}
        </Stack>
      ),
    },
    {
      id: 'effectiveFrom',
      headerKey: 'cat:prices.columns.effective_from',
      cell: (row) => <JalaliDate value={row.effectiveFrom} />,
    },
    { id: 'itemCount', headerKey: 'cat:tariff.columns.item_count', cell: (row) => faDigits(row.itemCount) },
    {
      id: 'status',
      headerKey: 'cat:tariff.columns.status',
      cell: (row) =>
        row.status ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <StatusChip
              tone={row.status === 'Active' ? 'active' : row.status === 'Superseded' ? 'cancelled' : 'pending'}
              labelKey={`cat:tariff.status.${row.status}`}
            />
            {row.activatedAutomatically && (
              <Typography variant="caption" color="text.secondary">
                {t('cat:tariff.automatic')}
              </Typography>
            )}
          </Stack>
        ) : (
          <StatusChip tone="pending" labelKey="cat:tariff.status.NotAccepted" />
        ),
    },
    {
      id: 'actions',
      headerKey: 'cat:prices.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => setImpactOf(row)} data-testid={`tariff-impact-${row.dataset}-${row.version}`}>
            {t('cat:tariff.impact')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!write.canWrite || row.status === 'Active' || row.status === 'Superseded'}
            onClick={() => setActivating(row)}
            data-testid={`tariff-activate-${row.dataset}-${row.version}`}
          >
            {t('cat:tariff.activate')}
          </Button>
        </Stack>
      ),
    },
  ];

  const impactColumns: DataTableColumn<TariffImpactRow>[] = [
    { id: 'code', headerKey: 'cat:services.columns.code', cell: (row) => <bdi dir="ltr">{row.serviceCode}</bdi> },
    { id: 'name', headerKey: 'cat:services.columns.name', cell: (row) => row.serviceName },
    { id: 'nationalCode', headerKey: 'cat:coding.national_code', cell: (row) => <bdi dir="ltr">{row.nationalCode}</bdi> },
    {
      id: 'current',
      headerKey: 'cat:tariff.columns.current_tariff',
      cell: (row) =>
        row.currentTariff ? (
          <Money amountMinor={row.currentTariff.amount} currency={row.currentTariff.currency} />
        ) : (
          t('cat:tariff.not_computable')
        ),
    },
    {
      id: 'next',
      headerKey: 'cat:tariff.columns.next_tariff',
      cell: (row) =>
        row.isRemoved ? (
          <StatusChip tone="cancelled" labelKey="cat:tariff.removed" />
        ) : row.nextTariff ? (
          <span data-testid={`tariff-next-${row.nationalCode}`}>
            <Money amountMinor={row.nextTariff.amount} currency={row.nextTariff.currency} />
          </span>
        ) : (
          t('cat:tariff.not_computable')
        ),
    },
    {
      id: 'change',
      headerKey: 'cat:tariff.columns.change',
      cell: (row) =>
        percent(row.changeBasisPoints) === null
          ? '—'
          : t('cat:tariff.percent_value', { value: percent(row.changeBasisPoints) }),
    },
  ];

  const comparisonColumns: DataTableColumn<TariffComparisonRow>[] = [
    { id: 'code', headerKey: 'cat:services.columns.code', cell: (row) => <bdi dir="ltr">{row.serviceCode}</bdi> },
    { id: 'name', headerKey: 'cat:services.columns.name', cell: (row) => row.serviceName },
    {
      id: 'center',
      headerKey: 'cat:tariff.columns.center_price',
      cell: (row) =>
        row.centerPrice ? (
          <Money amountMinor={row.centerPrice.amount} currency={row.centerPrice.currency} />
        ) : (
          t('cat:prices.not_defined')
        ),
    },
    {
      id: 'approved',
      headerKey: 'cat:tariff.columns.approved_tariff',
      cell: (row) =>
        row.approvedTariff ? (
          <Money amountMinor={row.approvedTariff.amount} currency={row.approvedTariff.currency} />
        ) : (
          t('cat:tariff.not_computable')
        ),
    },
    {
      id: 'difference',
      headerKey: 'cat:tariff.columns.difference',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span data-testid={`tariff-diff-${row.serviceCode}`}>
            {percent(row.differenceBasisPoints) === null
              ? '—'
              : t('cat:tariff.percent_value', { value: percent(row.differenceBasisPoints) })}
          </span>
          {row.isAbove && <StatusChip tone="cancelled" labelKey="cat:tariff.above" />}
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_204}
      titleKey="cat:tariff.title"
      subtitleKey="cat:tariff.subtitle"
      filters={
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            select
            size="small"
            label={t('cat:tariff.branch')}
            value={branchId}
            onChange={(event) => setBranchId(event.target.value)}
            sx={{ minWidth: 220 }}
            slotProps={{ htmlInput: { 'data-testid': 'tariff-branch' } as object }}
          >
            <MenuItem value="">{t('cat:tariff.branch_tenant')}</MenuItem>
            {branches.branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.name}
              </MenuItem>
            ))}
          </TextField>
          <WriteNotice write={write} />
        </Stack>
      }
    >
      <Stack spacing={2}>
        {versions.data && !versions.data.autoActivate && (
          <Alert severity="info" data-testid="tariff-auto-off">
            {t('cat:tariff.auto_off')}
          </Alert>
        )}

        <DataTable
          captionKey="cat:tariff.versions_caption"
          columns={versionColumns}
          rows={versions.data?.items}
          getRowId={(row) => `${row.dataset}-${row.version}`}
          isLoading={versions.isPending}
          error={versions.error}
          onRetry={() => void versions.refetch()}
          emptyTitleKey="cat:tariff.versions_empty"
        />

        {impactOf !== null && (
          <Paper variant="outlined" sx={{ p: 2 }} data-testid="tariff-impact-panel">
            <Stack spacing={1.5}>
              <Typography variant="subtitle1">
                {t('cat:tariff.impact_title', {
                  dataset: t(`cat:tariff.dataset.${impactOf.dataset}`),
                  version: faDigits(impactOf.version),
                })}
              </Typography>
              {impact.data && Number(impact.data.removedCount) > 0 && (
                <Alert severity="warning" data-testid="tariff-removed-count">
                  {t('cat:tariff.removed_count', { count: faDigits(impact.data.removedCount) })}
                </Alert>
              )}
              <DataTable
                captionKey="cat:tariff.impact_caption"
                columns={impactColumns}
                rows={impact.data?.rows}
                getRowId={(row) => row.serviceId}
                isLoading={impact.isPending}
                error={impact.error}
                onRetry={() => void impact.refetch()}
                emptyTitleKey="cat:tariff.impact_empty"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  disabled={!write.canWrite || impactOf.status === 'Active'}
                  onClick={() => setActivating(impactOf)}
                  data-testid="tariff-impact-activate"
                >
                  {t('cat:tariff.activate')}
                </Button>
                <Button onClick={() => setImpactOf(null)}>{t('common:actions.close')}</Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">{t('cat:tariff.comparison_title')}</Typography>
            {comparison.data && Number(comparison.data.aboveCount) > 0 && (
              <Alert severity="warning" data-testid="tariff-above-count">
                {t('cat:tariff.above_count', { count: faDigits(comparison.data.aboveCount) })}
              </Alert>
            )}
            {comparison.data?.isSample && <SampleBadge />}
            <DataTable
              captionKey="cat:tariff.comparison_caption"
              columns={comparisonColumns}
              rows={comparison.data?.rows}
              getRowId={(row) => row.serviceId}
              isLoading={comparison.isPending}
              error={comparison.error}
              onRetry={() => void comparison.refetch()}
              emptyTitleKey="cat:tariff.comparison_empty"
            />
          </Stack>
        </Paper>
      </Stack>

      <ConfirmDialog
        open={activating !== null}
        titleKey="cat:tariff.activate_title"
        messageKey="cat:tariff.activate_message"
        messageValues={{
          dataset: activating ? t(`cat:tariff.dataset.${activating.dataset}`) : '',
          version: activating ? faDigits(activating.version) : '',
        }}
        confirmKey="cat:tariff.activate"
        busy={activate.isPending}
        error={activate.error}
        onConfirm={() => activating && activate.mutate(activating)}
        onCancel={() => setActivating(null)}
      />
    </ListPage>
  );
}
